// D183.1 (meeting 2026-10-30): how far an order has been billed by its suppliers and what it has earned so
// far, worked out ONCE here (BUSINESS_RULES#client-invoice-live-progress). Client only: nothing on the server
// reads it (the server only supplies `entered`, D183.2). Pure; missing is null, never 0.
//
// Each line is in exactly one state:
//  - closed:  marked Not supplied, billed nothing of an ordered line, or nothing left to supply after a
//             short-delivery credit. It adds nothing to any money figure.
//  - billed:  its cost is a supplier's real one (costSourceTone 'positive': bill, stock sent, set on the line).
//  - entered: not billed yet, but a vendor bill the PO reconciliation pairs to it has been entered (provisional;
//             stored cost, stock and AP still change only on Confirm & sync). Its cost is that bill's unit cost.
//  - hand:    a cost typed by hand (or a costed line with no source). Not a supplier bill.
//  - waiting: still on the PO or quoted price, or no cost at all.
// Profit is worked out on billed and entered lines only, so an estimate never counts. The row figures come from
// `invoiceProfit` (selling net of credit notes, cost) and the margin formulas are `lineMargin`'s: no copy here.
// Short supply (owner 2026-11-02): billed value is pro-rata on the billed quantity, capped at the order quantity.
// Actual profit reads ONE quantity per counted line: actualQty q = what was actually sold and billed (owner
// 2026-11-02: 12 of 10 ordered counts all 12), never the order-capped billedQty. q = the supplier's billed qty
// (stored `supplierBilledQty`, or the entered bill's qty; else all of it), capped at sellableQty, the quantity the
// client is charged for (invoiced - credited short). Selling AND cost are both worked on q, so they cannot drift
// apart: selling = the row's selling (already net of credit notes) x q / sellableQty; cost = unit cost x q, the
// unit cost being the row cost / invoiced qty (billed line; the supplier billed the full invoiced qty) or the
// entered bill's unit cost. Progress (billedQty, billedValue) stays against the vessel's order, capped at the
// order qty, and never reads q. A line with nothing left to charge (sellableQty <= 0, credited away) is closed.
import { num, round2, roundQty } from '../../utils/invoiceLineMath';
import { invoicedQty } from '../../utils/clientInvoiceDispatch';
import { isMarkedNotSupplied, isNotSuppliedLine } from '../../utils/clientInvoiceLine';
import { lineMargin, sumOrNull, type ProfitRow } from '../../utils/clientInvoiceProfit';
import { costSourceTone } from './costSourceLabels';

export type LineProgressState = 'billed' | 'entered' | 'waiting' | 'closed' | 'hand';

/** From GET /api/client-invoices/:id/live-progress (D183.2). */
export interface EnteredCost {
  lineNumber: number;
  /** Per invoice unit, invoice currency, VAT-inclusive (the same basis as actualUnitCost). */
  unitCost: number | null;
  /** Quantity on the entered bill(s), invoice unit. */
  qty: number | null;
  /** Vendor invoice numbers, for the tag tooltip. */
  invoiceNumbers: string[];
}

export interface LineProgress {
  lineNumber: number;
  label: string;
  state: LineProgressState;
  orderQty: number | null;
  orderValue: number | null;
  billedQty: number | null;
  billedValue: number | null;
  /** The one quantity selling and cost are worked on: what was sold and billed, capped at invoiced - credited short (not at the order). Null unless billed or entered. */
  actualQty: number | null;
  selling: number | null;
  cost: number | null;
  margin: number | null;
  marginPct: number | null;
  markupPct: number | null;
  /** Vendor invoice numbers when state === 'entered', else []. */
  enteredFrom: string[];
}

export interface OrderProgress {
  lines: LineProgress[];
  orderValue: number | null;
  /** Billed and entered lines (provisional bills count); `enteredValue` is the entered part of it. */
  billedValue: number | null;
  enteredValue: number | null;
  remainingValue: number | null;
  progressPct: number | null;
  counts: Record<LineProgressState, number>;
  selling: number | null;
  cost: number | null;
  margin: number | null;
  marginPct: number | null;
  markupPct: number | null;
  final: boolean;
}

export function orderProgress(
  items: ReadonlyArray<any>,
  rows: ReadonlyArray<ProfitRow>,
  creditedShortByLine: ReadonlyMap<string, number> | null,
  enteredByLine: ReadonlyMap<number, EnteredCost> | null,
): OrderProgress {
  const list = Array.isArray(items) ? items : [];
  const counts: Record<LineProgressState, number> = { billed: 0, entered: 0, waiting: 0, closed: 0, hand: 0 };

  const lines: LineProgress[] = list.map((item, index) => {
    const row: ProfitRow | undefined = rows?.[index];
    const lineNumber = row?.lineNumber ?? num(item?.lineNumber) ?? index + 1;
    const label = item?.itemName || item?.itemDescription || `Line ${index + 1}`;
    const creditedShort = Math.max(0, num(creditedShortByLine?.get(String(lineNumber))) ?? 0);
    const orderQty = Math.max(0, roundQty((num(item?.confirmedQuantity) ?? invoicedQty(item)) - creditedShort));
    const unit = num(item?.quoteUnitPrice);
    const rowCost = row?.cost ?? null;
    const invoiced = invoicedQty(item);
    const sellable = Math.max(0, roundQty(invoiced - creditedShort)); // what the client is charged for, net of short credits

    let state: LineProgressState;
    const entered = enteredByLine?.get(lineNumber) ?? null;
    const enteredUnitCost = num(entered?.unitCost);
    const tone = costSourceTone(item || {})?.tone;
    if (isMarkedNotSupplied(item) || isNotSuppliedLine(item) || orderQty === 0 || sellable === 0) state = 'closed';
    else if (tone === 'positive') state = 'billed';
    else if (entered && enteredUnitCost !== null) state = 'entered';
    else if (tone === 'neutral' || (tone === undefined && rowCost !== null)) state = 'hand';
    else state = 'waiting';
    counts[state] += 1;

    const counted = state === 'billed' || state === 'entered';
    let billedQty: number | null = null;
    if (state === 'billed') {
      const stored = num(item?.supplierBilledQty);
      billedQty = roundQty(Math.min(stored !== null && stored > 0 ? stored : orderQty, orderQty));
    } else if (state === 'entered') {
      billedQty = roundQty(Math.min(Math.max(0, num(entered?.qty) ?? orderQty), orderQty));
    }

    const open = state !== 'closed';
    const orderValue = open && unit !== null ? round2(unit * orderQty) : null;
    const billedValue = counted && unit !== null ? round2(unit * billedQty!) : null;

    let selling: number | null = null;
    let cost: number | null = null;
    let actualQty: number | null = null;
    if (counted) {
      // q is what was sold and billed, never the order-capped billedQty (owner 2026-11-02: 12 of 10 ordered counts 12).
      let q: number;
      if (state === 'billed') {
        const stored = num(item?.supplierBilledQty);
        q = Math.min(stored !== null && stored > 0 ? stored : sellable, sellable);
      } else q = Math.min(Math.max(0, num(entered?.qty) ?? sellable), sellable);
      q = roundQty(q);
      actualQty = q;
      selling = row?.selling == null ? null : round2((row.selling * q) / sellable); // sellable > 0: a line with none is closed
      let c: number | null;
      if (state === 'billed') c = rowCost === null ? null : round2((rowCost / invoiced) * q); // invoiced >= sellable > 0
      else c = round2(enteredUnitCost! * q);
      cost = c === 0 ? null : c; // a cost of 0 is no cost
    }
    const { margin, marginPct, markupPct } = lineMargin(selling, cost);

    return {
      lineNumber, label, state,
      orderQty, orderValue, billedQty, billedValue, actualQty,
      selling, cost, margin, marginPct, markupPct,
      enteredFrom: state === 'entered' ? [...(entered?.invoiceNumbers || [])] : [],
    };
  });

  const margined = lines.filter((l) => l.margin !== null);
  const orderValue = sumOrNull(lines.map((l) => l.orderValue));
  const billedValue = sumOrNull(lines.map((l) => l.billedValue));
  const enteredValue = sumOrNull(lines.filter((l) => l.state === 'entered').map((l) => l.billedValue));
  const selling = sumOrNull(margined.map((l) => l.selling));
  const cost = sumOrNull(margined.map((l) => l.cost));
  const { marginPct, markupPct } = lineMargin(selling, cost);
  const progress = orderValue !== null && orderValue > 0 ? Math.round(((billedValue ?? 0) / orderValue) * 1000) / 10 : null;

  return {
    lines,
    orderValue,
    billedValue,
    enteredValue,
    remainingValue: orderValue === null ? null : round2(orderValue - (billedValue ?? 0)),
    progressPct: progress === null ? null : Math.min(100, Math.max(0, progress)),
    counts,
    selling,
    cost,
    margin: sumOrNull(margined.map((l) => l.margin)),
    marginPct,
    markupPct,
    final:
      counts.billed > 0 && counts.entered === 0 && counts.waiting === 0 && counts.hand === 0 &&
      lines.every((l) => l.state !== 'billed' || l.margin !== null),
  };
}
